import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "335",
  "slug": "why-is-north-up-on-maps",
  "title": "Why Is North Always Up on Maps? It Wasn't Always",
  "excerpt": "There's no physical reason north belongs at the top of a map. The convention is younger than you'd think — and for most of history, maps pointed somewhere else entirely.",
  "date": "2026-06-03",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1524661135-423995f22d0b?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxhbnRpcXVlJTIwd29ybGQlMjBtYXB8ZW58MXwwfHx8MTc4MDM5MzI3Mnww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "5 min",
  "author": "EarthGuessr Team",
  "tldr": "North-up is a convention, not a law of nature. Medieval European maps usually put east at the top — which is why we still say we 'orient' a map — many Islamic maps faced south, and north only became standard with the magnetic compass, the Renaissance revival of Ptolemy, and European sea charts.",
  "content": [
    {
      "type": "paragraph",
      "text": "Pick up almost any map made today and north is at the top. It feels so natural that it's easy to assume it has to be that way — that north is somehow the real top of the world. It isn't. In space there is no up or down, and Earth doesn't have a top. North-up is a habit, a few centuries old, that we've collectively agreed to. For most of recorded history, mapmakers put something else at the top entirely."
    },
    {
      "type": "heading",
      "text": "'Orient' Literally Means 'To Face East'"
    },
    {
      "type": "paragraph",
      "text": "When you orient a map, you're using a word that quietly preserves the old convention. In medieval Europe, many maps were drawn with east at the top — the direction of the rising sun, and for Christian mapmakers the direction of the Garden of Eden. The famous T-O maps of the period placed east up top, with Jerusalem near the centre of the world. To orient a map originally meant to line it up with the orient: the east."
    },
    {
      "type": "heading",
      "text": "Other Cultures, Other Tops"
    },
    {
      "type": "paragraph",
      "text": "East-up was far from universal. Many medieval Islamic cartographers, including the great 12th-century mapmaker al-Idrisi, drew their maps with south at the top, so that to a reader in the Mediterranean world, the familiar lands sat along the bottom. Different traditions made different choices, and none of them was wrong — each simply reflected where its makers stood and what they cared about pointing toward."
    },
    {
      "type": "heading",
      "text": "How North Won"
    },
    {
      "type": "paragraph",
      "text": "Three things pushed north to the top. First, the magnetic compass: as it spread through medieval seafaring, a needle that reliably pointed north made north the natural reference direction for navigation. Second, the Renaissance rediscovery of Ptolemy's ancient Geographia, which was reconstructed with north at the top and became hugely influential among European scholars. Third, the age of European sea charts and, later, the Mercator projection — built for north-up navigation — which spread on printed maps across the world. Once the printing press locked the convention into millions of identical copies, north-up became simply how maps look."
    },
    {
      "type": "heading",
      "text": "Does It Actually Matter?"
    },
    {
      "type": "paragraph",
      "text": "More than you'd guess. Putting north at the top, combined with a Mercator-style projection that inflates the high latitudes, subtly trains us to read the northern hemisphere as 'up' and 'above' the rest of the world — and our language treats up as better, higher, superior. That's why south-up maps can feel so jarring: nothing has actually moved, but a lifetime of assumptions suddenly shows itself."
    },
    {
      "type": "heading",
      "text": "South-Up and the Politics of the Top"
    },
    {
      "type": "paragraph",
      "text": "In 1979 an Australian named Stuart McArthur published a map with south at the top, partly as a cheerful protest against forever seeing his country stuck in the bottom corner of the world. It's the same map, the same coastlines, the same data — and it feels deeply wrong to most viewers, which is exactly the point. Putting the southern hemisphere on top doesn't change a single fact about geography; it only exposes how strongly the convention has shaped what we expect to see."
    },
    {
      "type": "heading",
      "text": "Your Phone Still Picks a Top"
    },
    {
      "type": "paragraph",
      "text": "The convention is alive and well in your pocket. Open a maps app and, by default, north is up — until you switch to the mode that rotates the map to match the way you're facing, which many people find far more intuitive for actually walking somewhere. That little toggle is the whole history of this question in miniature: north-up is excellent for a shared, standardised reference, but it isn't always the most natural way for a human being to find their way."
    },
    {
      "type": "heading",
      "text": "Maps That Had No North at All"
    },
    {
      "type": "paragraph",
      "text": "Not every navigation tradition even used a top-down, north-fixed map. Polynesian wayfinders crossed thousands of kilometres of open Pacific using star paths, swell patterns, and bird behaviour, with no paper chart in sight. Medieval Mediterranean sailors navigated with portolan charts criss-crossed by lines radiating from compass roses, designed to be read along a bearing rather than from a single top. The idea that a map must be a bird's-eye rectangle with north at the top is just one solution among many that humans have invented for the problem of knowing where you are."
    },
    {
      "type": "heading",
      "text": "What This Means for Reading Maps"
    },
    {
      "type": "paragraph",
      "text": "The practical takeaway is to hold the frame a little more loosely. When you look at any map, remember that someone chose the centre, the projection, and the direction of up — and those choices quietly steer what you notice and what you overlook. A good map-reader mentally re-centres, asks what's being pushed to the edge, and treats north-up as a convenience rather than a truth. It's a small habit that makes you harder to mislead."
    },
    {
      "type": "paragraph",
      "text": "Check the north arrow before reading an unfamiliar map. If the map is rotated, use the compass directions when describing a location rather than assuming that the top of the page is north."
    }
  ]
};
